import axios from "axios";
import { getAccessToken } from "../utils/tokenManager";

const stripTrailingSlash = (value) => String(value || "").replace(/\/+$/, "");

const normalizeBaseUrl = (rawUrl, moduleName, fallbackUrl) => {
  const safeFallback = stripTrailingSlash(fallbackUrl);
  const safeRaw = stripTrailingSlash(rawUrl);

  if (!safeRaw) {
    return safeFallback;
  }

  try {
    const parsed = new URL(safeRaw);

    // In local dev the HRMS frontend often runs on :3000 while the backend runs
    // on :8888. If the frontend URL is mistakenly configured here, convert it to
    // the backend origin so plan/permission APIs hit HRMS server instead of CRM.
    if (
      moduleName === "hrms" &&
      parsed.port === "3000" &&
      (parsed.hostname === "localhost" || parsed.hostname === "127.0.0.1")
    ) {
      parsed.port = "8888";
    }

    return stripTrailingSlash(parsed.origin);
  } catch (error) {
    return safeRaw || safeFallback;
  }
};

const getBaseUrl = (module) => {
  const moduleName = String(module || "").toLowerCase();
  const fallbackApi = process.env.REACT_APP_API_URL;
  const baseUrls = {
    hrms: process.env.REACT_APP_HRMS_BASE_URL,
    pms: process.env.REACT_APP_PMS_BASE_URL,
    crm: process.env.REACT_APP_CRM_BASE_URL,
    econnect: process.env.REACT_APP_ECONNECT_BASE_URL,
    payroll: process.env.REACT_APP_PAYROLL_BASE_URL,
    default: fallbackApi,
  };

  return normalizeBaseUrl(baseUrls[moduleName] || baseUrls.default, moduleName, fallbackApi);
};

const createAxiosInstance = (baseURL) => {
  return axios.create({
    baseURL,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      platform: "web-client",
      "x-super-admin":"true"
    },
  });
};

const defaultAxiosInstance = createAxiosInstance(process.env.REACT_APP_API_URL);

const addInterceptors = (instance) => {
  instance.interceptors.request.use(
    (config) => {
      const token = getAccessToken();
      if (token) {
        config.headers["authorization"] = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        localStorage.clear();
        window.location.href = "/";
      }
      return Promise.reject(error);
    }
  );
};

addInterceptors(defaultAxiosInstance);

export default class ApiService {
  static abortControllers = new Map();
  static instanceCache = new Map();

  static getAxiosInstance(module) {
    if (!module) {
      return defaultAxiosInstance;
    }

    const cacheKey = module.toLowerCase();
    
    if (!this.instanceCache.has(cacheKey)) {
      const baseURL = getBaseUrl(module);
      const instance = createAxiosInstance(baseURL);
      addInterceptors(instance);
      this.instanceCache.set(cacheKey, instance);
    }

    return this.instanceCache.get(cacheKey);
  }

  static async request(method, url, body, config = {}) {
    const { module, skipCancelToken = false, ...restConfig } = config;
    const axiosInstance = this.getAxiosInstance(module);
    
    const abortKey = module ? `${module}-${url}` : url;

    if (!skipCancelToken && this.abortControllers.has(abortKey)) {
      const existingController = this.abortControllers.get(abortKey);
      existingController?.abort();
    }

    const controller = new AbortController();

    if (!skipCancelToken) {
      this.abortControllers.set(abortKey, controller);
    }

    try {
      const response = await axiosInstance({
        method,
        url,
        data: body,
        ...restConfig,
        headers: {
          ...restConfig.headers,
        },
        signal: controller.signal,
      });

      return response.data;
    } catch (err) {
      if (err.name === "AbortError") {
        console.log(`Request aborted: ${abortKey}`);
      }
      throw err;
    } finally {
      if (!skipCancelToken && this.abortControllers.get(abortKey) === controller) {
        this.abortControllers.delete(abortKey);
      }
    }
  }

  static get(url, config = {}) {
    return this.request("get", url, undefined, config);
  }

  static post(url, body, config = {}) {
    return this.request("post", url, body, config);
  }

  static put(url, body, config = {}) {
    return this.request("put", url, body, config);
  }

  static delete(url, config = {}) {
    return this.request("delete", url, undefined, config);
  }
}